//@ts-strict-ignore
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import GridLayout from 'react-grid-layout';
import DashboardCard from './DashboardCard';
import { Button } from '../../components/connectedForm/Button';
import { default as Form } from '../connectedForm';
import Widget, { IWidgetProps } from './Widget';
import * as input from '../../components/connectedForm/inputComponents';
import DiaryEventWorkspace from '../../views/diary/DiaryEventWorkspace';
import fieldMetaData, { IEnumMetaData } from '../../graphql/schema/fieldMetaData';
import ColourPicker from '../connectedForm/ColourPicker/ColourPicker';
import * as entities from '../../graphql/entities';
import ViewsQuery from '../../graphql/queries/view/ViewsQuery.graphql';
import { compose } from 'recompose';
import Label from '../connectedForm/Label';
import Slider from '../connectedForm/Slider';
import * as withContainer from '../../graphql/containers';
import WidgetDefault from '../../graphql/queries/dashboard/WidgetDefaultQuery.graphql';
import getIn from '../connectedForm/utils/getIn';
import { getGroupFields, getNumericFields } from '../../utils/FieldUtil';
import SalesInvoiceAnalysis, {
  ANALYTICS_REPORT_TYPES,
  STANDARD_MEASURES,
} from '../../views/salesLedger/salesInvoiceAnalysis/SalesInvoiceAnalysis';
import { useDispatch, useSelector } from 'react-redux';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { PrimaryButton } from '../connectedForm/Button';
import Fieldset from '../connectedForm/Fieldset';
import Row from '../connectedForm/Row';
import {
  CostViewPermission,
  DashboardWidgetType,
  IDashboardWidget,
  IDashboardWidgetDefaultQueryResult,
  ISalesAnalyticsHistoryInput,
  AnalysisMeasure,
} from '../../graphql/schema/p4types';
import NivoPie, { NivoPieData } from './widgets/NivoPie';
import { OGL_COLOURS } from '../../styles/themes/default/widgetColours';
import NivoBar from './widgets/NivoBar';
import NivoProgress from './widgets/NivoProgress';
import NivoGauge from './widgets/NivoGauge';
import NumberWidget from './widgets/NumberWidget';
import { IDropdownProps } from '../../@types/dropdown';
import { ICheckboxInputProps } from '../connectedForm/CheckboxInput/CheckboxInput';
import { BlurTextFieldType, IBlurTextFieldProps } from '../connectedForm/BlurTextField/BlurTextField';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from '../modals/Styles';
import { WidgetWorkspace } from '../../@types/enums';
import { IObjectField } from '../../graphql/schema/getQueryFields';
import { EntityType } from '../../@types/entityTypes';
import { DocumentNode } from 'graphql/index';

const GridContainer = styled.div`
  margin-top: 5px;
  padding-bottom: 100px;
  height: 100%;
  overflow: auto;
`;

//Can later add: '0%', '0.00%', '0.000%'. Doesn't make much sense without an 'average' operation available though.
const ValueMaskOptions = ['0,0', '0,0.00', '$0,0', '$0,0.00', '($0a)'].map((code) => {
  return { label: code.replace('$', '£'), value: code };
});

type IEditControlsType = {
  widget: any;
  dataset: any;
  dataid: string;
  datasetId: string;
  skipField?: JSX.Element | null;
};

type IFieldConfigType = {
  entityType: EntityType;
  altQuery?: DocumentNode;
} | null;

const getFieldConfig = (link: WidgetWorkspace): IFieldConfigType => {
  let entityType: EntityType;
  let altQuery: DocumentNode | undefined;

  switch (link) {
    case WidgetWorkspace.Customers:
      entityType = entities.Customer;
      break;
    case WidgetWorkspace.SalesOrders:
      entityType = entities.SalesOrder;
      break;
    case WidgetWorkspace.SalesQuotes:
      entityType = entities.SalesOrder;
      altQuery = entities.SalesOrder.quoteWorkspace;
      break;
    case WidgetWorkspace.SalesOrdersAndQuotes:
      entityType = entities.SalesOrder;
      altQuery = entities.SalesOrder.combinedWorkspace;
      break;
    case WidgetWorkspace.Tasks:
      entityType = entities.DiaryEvent;
      break;
    case WidgetWorkspace.Suppliers:
      entityType = entities.Supplier;
      break;
    case WidgetWorkspace.SalesInvoices:
      entityType = entities.SalesInvoice;
      break;
    case WidgetWorkspace.SalesInvoiceLines:
      entityType = entities.SalesInvoiceLine;
      break;
    case WidgetWorkspace.SalesReceipts:
      entityType = entities.SalesReceipt;
      break;
    case WidgetWorkspace.NominalAccounts:
      entityType = entities.Nominal;
      break;
    case WidgetWorkspace.NominalTransactions:
      entityType = entities.NominalTransaction;
      break;
    case WidgetWorkspace.BankAccounts:
      entityType = entities.Bank;
      break;
    case WidgetWorkspace.Products:
      entityType = entities.Product;
      break;
    case WidgetWorkspace.PurchaseOrders:
      entityType = entities.PurchaseOrder;
      break;
    case WidgetWorkspace.SalesOrderItems:
      entityType = entities.SalesOrderLine;
      break;
    case WidgetWorkspace.SalesCredits:
      entityType = entities.SalesCredit;
      break;
    case WidgetWorkspace.SalesCreditLines:
      entityType = entities.SalesCreditLine;
      break;
    case WidgetWorkspace.CustomerRefunds:
      entityType = entities.SalesRefund;
      break;
    case WidgetWorkspace.CustomerBadDebts:
      entityType = entities.SalesBadDebt;
      break;
    case WidgetWorkspace.SalesTransactions:
      entityType = entities.SalesTransaction;
      break;
    case WidgetWorkspace.ImportedAmazonOrders:
      entityType = entities.ImportedAmazonOrder;
      break;
    case WidgetWorkspace.ImportedEbayOrders:
      entityType = entities.ImportedEbayOrder;
      break;
    case WidgetWorkspace.ImportedShopifyOrders:
      entityType = entities.ImportedShopifyOrder;
      break;
    case WidgetWorkspace.ImportedWooCommerceOrders:
      entityType = entities.ImportedWooCommerceOrder;
      break;
    case WidgetWorkspace.ImportedPixSellOrders:
      entityType = entities.ImportedPixSellOrder;
      break;
    case WidgetWorkspace.NominalAccrualsAndPrepayments:
      entityType = entities.NominalAccrualPrepayment;
      break;
    case WidgetWorkspace.VatTransactions:
      entityType = entities.VatTransaction;
      break;
    case WidgetWorkspace.PurchaseTransactions:
      entityType = entities.PurchaseTransaction;
      break;
    case WidgetWorkspace.PurchaseInvoices:
      entityType = entities.PurchaseInvoice;
      break;
    default:
      return null;
  }

  return { entityType, altQuery };
};

const useAvailableSumFields = (link: WidgetWorkspace): IObjectField[] => {
  const fieldConfig = getFieldConfig(link);
  let fields: IObjectField[] = [];
  if (fieldConfig) {
    fields = getNumericFields(fieldConfig.entityType, fieldConfig.altQuery);
  }

  return usePermissionFilteredFields(fields);
};

const useAvailableGroupFields = (link: WidgetWorkspace) => {
  const fieldConfig = getFieldConfig(link);
  let fields: IObjectField[] = [];
  if (fieldConfig) {
    fields = getGroupFields(fieldConfig.entityType, fieldConfig.altQuery);
  }

  return usePermissionFilteredFields(fields);
};

const EditControls = ({ widget, dataset, dataid, datasetId, skipField = null }: IEditControlsType) => {
  const user = useSelector((store: any) => store.app.user);
  const availableSumFields = useAvailableSumFields(dataset?.link);
  const availableGroupFields = useAvailableGroupFields(dataset?.link);

  const { data, loading } = useQuery(ViewsQuery, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      systemUserId: user.id,
      screen: dataset?.link || null,
    },
  });

  const workspaceDropdown = (
    <Form.Field<IDropdownProps>
      dataid={`${datasetId}.link`}
      component={input.Dropdown}
      label={'Workspace'}
      enum={true}
      options={(fieldMetaData.WidgetWorkspace as IEnumMetaData<WidgetWorkspace>).enumValues}
      mandatory={true}
      hint={'A workspace must be chosen to use as the data source for a widget'}
      data-testid="edit-widget-workspace"
    />
  );

  if (loading || !data) return <Form.Fieldset cols={1}>{workspaceDropdown}</Form.Fieldset>;

  const viewNodes = data.data.edges.map((edge) => edge.node);
  let groupField;

  const groupFieldOptions = availableGroupFields;
  const sumFieldOptions = availableSumFields;

  if ('PIE,DOUGHNUT,BAR'.includes(widget.type)) {
    groupField = (
      <Form.Field<IDropdownProps>
        dataid={`${datasetId}.groupColumn`}
        component={input.Dropdown}
        label={'Group By'}
        labelKey={'name'}
        valueKey={'fieldName'}
        valueOnly={true}
        options={groupFieldOptions}
        dynamicOptions={true}
        mandatory={true}
        hint={
          "A field must be chosen as the 'group by' for the field, this would be the 'X Axis' of a bar chart - Commonly this would be a 'reference' column (Customer Reference, Product Reference, etc)."
        }
        data-testid="edit-widget-group-by"
      />
    );
  }

  const sumField =
    widget.operation !== 'COUNT' && !'WORKSPACE'.includes(widget.type) ? ( //Using !Count for the moment as only 2 operations and this is the default.
      <Form.Field<IDropdownProps>
        dataid={`${datasetId}.sumColumn`}
        component={input.Dropdown}
        label={'Sum Column'}
        labelKey={'name'}
        valueKey={'fieldName'}
        valueOnly={true}
        options={sumFieldOptions}
        dynamicOptions={true}
        mandatory={true}
        hint={
          "A numeric column must be chosen as the source of values for the widget, this would be the 'Y Axis' of a bar chart - For example 'nett line value' or 'balance'."
        }
        data-testid="edit-widget-sum-column"
      />
    ) : null;

  const operationField = 'WORKSPACE'.includes(widget.type) ? null : (
    <Form.Field
      dataid={`${datasetId}.operation`}
      component={input.Dropdown}
      label={'Operation'}
      mandatory={true}
      data-testid="edit-widget-operation"
    />
  );

  let graphTypeNodes: Array<{ type: DashboardWidgetType }> = [];
  switch (widget.type) {
    case DashboardWidgetType.Number:
    case DashboardWidgetType.Progress:
    case DashboardWidgetType.Gauge:
    case DashboardWidgetType.Gaugecircle:
      graphTypeNodes = [
        DashboardWidgetType.Number,
        DashboardWidgetType.Progress,
        DashboardWidgetType.Gauge,
        DashboardWidgetType.Gaugecircle,
      ].map((t) => ({
        type: t,
      }));
      break;
    case DashboardWidgetType.Pie:
    case DashboardWidgetType.Doughnut:
    case DashboardWidgetType.Bar:
      graphTypeNodes = [DashboardWidgetType.Pie, DashboardWidgetType.Doughnut, DashboardWidgetType.Bar].map((t) => ({
        type: t,
      }));
      break;
  }

  return (
    <Form.Fieldset cols={1}>
      <Form.Row>
        <Form.Fieldset cols={0.5}>
          <Form.Legend label={'Data'} />
          {workspaceDropdown}
          <Form.Field<IDropdownProps>
            dataid={`${datasetId}.view`}
            component={input.Dropdown}
            label={'View'}
            labelKey={'name'}
            valueKey={'name'}
            valueOnly={true}
            options={viewNodes}
            mandatory={true}
            hint={'A view from the workspace must be chosen which is used to filter the data for the widget.'}
            data-testid="edit-widget-view"
          />
          {groupField}
          {operationField}
          {sumField}
        </Form.Fieldset>
        <Form.Fieldset cols={0.5}>
          {graphTypeNodes.length > 0 ? (
            <>
              <Form.Legend label={'Graph'} />
              <Form.Field<IDropdownProps>
                dataid={`${dataid}.type`}
                component={input.Dropdown}
                label={'Graph Type'}
                labelKey={'type'}
                valueKey={'type'}
                valueOnly={true}
                options={graphTypeNodes}
                mandatory={true}
                data-testid="edit-widget-graph-type"
              />
            </>
          ) : null}
          <Form.Legend label={'Layout/Frequency'} />
          {skipField}
          <Form.Field<any>
            dataid={`${datasetId}.interval`}
            component={Slider}
            label={'Update Frequency'}
            min={300000} //5 Minutes
            max={3600000} //1 Hour
            marks={{ 300000: '5m', 1800000: '30m', 3600000: '1h' }}
            step={300000}
            defaultValue={3600000}
            tipFormatter={(value) => `${value / 60000}m`}
            tipProps={{
              overlayStyle: {
                zIndex: 1000000,
              },
            }}
            data-testid="edit-widget-update-freq"
          />
        </Form.Fieldset>
      </Form.Row>
    </Form.Fieldset>
  );
};

const WidgetStyleConfiguration = ({ value, dataid }) => {
  if (value.type.includes('WORKSPACE')) return null;

  const valueMask = 'PIE,DOUGHNUT,BAR,PROGRESS,GAUGECIRCLE,NUMBER'.includes(value.type) ? (
    <Form.Field<IDropdownProps>
      dataid={`${dataid}.mainDataset.valueMask`}
      component={input.Dropdown}
      label={'Mask'}
      labelKey={'label'}
      valueKey={'value'}
      valueOnly={true}
      creatable={true}
      options={ValueMaskOptions}
      rawLabel={true /*Don't drop the formatting, it's important!*/}
      newOptionCreator={({ label, labelKey, valueKey }) => {
        //Custom option creator to convert '£' to '$' for the format required by numeraljs.
        const option: { [key: string]: string } = {};
        option[valueKey] = label.replace('£', '$');
        option[labelKey] = label;
        option.className = 'Select-create-option-placeholder';
        return option;
      }}
      data-testid="edit-widget-mask"
    />
  ) : null;

  const colour2Field = 'NUMBER'.includes(value.type) ? null : (
    <Form.Fieldset cols={1 / 3}>
      <Label>Graph Colour</Label>
      <Form.Field<any>
        dataid={`${dataid}.mainDataset.color2`}
        component={ColourPicker}
        includeLabel={false}
        colourSet={'foreground'}
        backgroundColor={value.backgroundColor}
        data-testid="edit-widget-graph-colour"
      />
    </Form.Fieldset>
  );

  const colourScheme = 'PIE,DOUGHNUT,BAR,PROGRESS,GAUGECIRCLE'.includes(value.type) ? (
    <Form.Field<IDropdownProps>
      dataid={`${dataid}.mainDataset.colourScheme`}
      label={'Colour Scheme'}
      component={input.Dropdown}
      enum={true}
      options={
        'PROGRESS,GAUGECIRCLE'.includes(value.type)
          ? (fieldMetaData.ColourScheme as IEnumMetaData).enumValuesLimited
          : (fieldMetaData.ColourScheme as IEnumMetaData).enumValues
      }
      data-testid="edit-widget-colour-scheme"
    />
  ) : null;

  return (
    <Form.Fieldset cols={1}>
      <Form.Legend label={'Style'} />
      {valueMask}
      {colourScheme}
      <Form.Row>
        <Form.Fieldset cols={1 / 3}>
          <Label>Text Colour</Label>
          <Form.Field<any>
            dataid={`${dataid}.mainDataset.color`}
            component={ColourPicker}
            includeLabel={false}
            colourSet={'foreground'}
            backgroundColor={value.backgroundColor}
            data-testid="edit-widget-text-colour"
          />
        </Form.Fieldset>
        {colour2Field}
        <Form.Fieldset cols={1 / 3}>
          <Label>Background Colour</Label>
          <Form.Field<any>
            dataid={`${dataid}.backgroundColor`}
            component={ColourPicker}
            includeLabel={false}
            data-testid="edit-widget-background-colour"
          />
        </Form.Fieldset>
      </Form.Row>
    </Form.Fieldset>
  );
};

const EditSection = ({ dataid, formChange, gridMetrics, value, handleDrillFilter, selectedId, onClose }) => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const costViewPermission = useCostViewPermission();

  if (!value || !gridMetrics) return null;

  const targetField = 'PROGRESS,GAUGECIRCLE'.includes(value.type) ? (
    <Form.Field dataid={`${dataid}.target`} component={input.BlurTextField} label={'Target'} />
  ) : null;

  const reportType = ANALYTICS_REPORT_TYPES.find((rt) => {
    return rt.value === getIn(value, `mainDataset.reportType`);
  });
  const graphType = getIn(value, 'mainDataset.graphType');

  const skipField = 'BAR'.includes(value.type) ? (
    <Form.Field<ICheckboxInputProps>
      dataid={`${dataid}.autoSkipLabels`}
      component={input.Checkbox}
      label="Auto Skip Labels"
      hint={'Skip bar chart labels when there is not enough space'}
      data-testid="edit-widget-auto-skip"
    />
  ) : null;

  const allowCalculation = false;

  let editFields;
  if (value.type === 'ANALYTICS') {
    editFields = (
      <Form.Fieldset cols={1}>
        <Form.Field<IDropdownProps>
          dataid={`${dataid}.mainDataset.reportType`}
          component={input.Dropdown}
          label={'Report Type'}
          labelKey={'description'}
          options={
            ANALYTICS_REPORT_TYPES.filter(
              (type) => type.value !== 'PIVOT'
            ) /*PIVOT would require loading the pivot table and drill down capability, not worth it.*/
          }
          valueOnly={true}
          onChange={(event, _value) => {
            const _reportType = ANALYTICS_REPORT_TYPES.find((rt) => rt.value === _value);
            dispatch(formChange(`${dataid}.mainDataset.graphType`, _reportType?.graphTypes[0].value));
          }}
        />
        <Form.Field<IDropdownProps>
          dataid={`${dataid}.mainDataset.dimension`}
          component={input.Dropdown}
          label={'Initial Dimension'}
          labelKey={'title'}
          options={[
            {
              title: 'Products',
              value: 'PRODUCT',
            },
            {
              title: 'Warehouse',
              value: 'WAREHOUSE',
            },
            {
              title: 'Customer',
              value: 'CUSTOMER',
            },
          ]}
          valueOnly={true}
          onChange={() => {
            handleDrillFilter({});
          }}
        />
        <Form.Field<IDropdownProps>
          dataid={`${dataid}.mainDataset.measure`}
          component={input.Dropdown}
          label={'Measure'}
          labelKey={'description'}
          options={STANDARD_MEASURES.filter((measure) => {
            return !(
              costViewPermission === CostViewPermission.None &&
              (measure.value === AnalysisMeasure.Profit || measure.value === AnalysisMeasure.Cost)
            );
          })}
          valueOnly={true}
        />
        {reportType && reportType.graphTypes.length > 0 ? (
          <>
            <Form.Field<IDropdownProps>
              dataid={`${dataid}.mainDataset.graphType`}
              component={input.Dropdown}
              label={'Graph Type'}
              labelKey={'description'}
              options={reportType.graphTypes}
              valueOnly={true}
              dynamicOptions={true}
            />
            <Form.Field<IBlurTextFieldProps>
              dataid={`${dataid}.mainDataset.recordsInGraph`}
              component={input.BlurTextField}
              label={'Number of Records'}
              type={BlurTextFieldType.Number}
              mandatory={true}
              hint={'How many data points to show in the widget graph, recommended range of 5-100 as appropriate'}
              data-testid="edit-widget-records-in-graph"
            />
            <Form.Field<IDropdownProps>
              dataid={`${dataid}.mainDataset.sortDirection`}
              component={input.Dropdown}
              options={fieldMetaData.Direction.enumValues}
              enum={true}
              mandatory={true}
              valueKey="value"
              labelKey="label"
              label="Sort Direction"
            />
          </>
        ) : null}
        {graphType === 'BAR' /*Bar uses a fixed colour scheme.*/ ? null : (
          <Form.Field<IDropdownProps>
            dataid={`${dataid}.mainDataset.colourScheme`}
            label={'Colour Scheme'}
            component={input.Dropdown}
            enum={true}
            options={(fieldMetaData.ColourScheme as IEnumMetaData).enumValues}
          />
        )}
      </Form.Fieldset>
    );
  } else {
    const dataset1Content = (
      <EditControls
        widget={value}
        dataset={value.mainDataset}
        dataid={dataid}
        datasetId={`${dataid}.mainDataset`}
        skipField={skipField}
      />
    );

    editFields = (
      <>
        {allowCalculation ? (
          <Form.Field<IDropdownProps>
            dataid={`${dataid}.calculation`}
            component={input.Dropdown}
            label={'Calculation'}
            enum={true}
            options={(fieldMetaData.DashboardWidgetCalculation as IEnumMetaData).enumValues}
            mandatory={true}
          />
        ) : null}
        {allowCalculation ? (
          <Form.TabPanel>
            <Form.TabContent id="dataset1" label="Primary Dataset" tabStyle={{ width: 200 }}>
              {dataset1Content}
            </Form.TabContent>
            <Form.TabContent id="dataset2" label="Secondary Dataset" tabStyle={{ width: 200 }}>
              <EditControls
                widget={value}
                dataset={value.secondaryDataset}
                dataid={dataid}
                datasetId={`${dataid}.secondaryDataset`}
              />
            </Form.TabContent>
          </Form.TabPanel>
        ) : (
          dataset1Content
        )}
      </>
    );
  }

  const { gridWidth, cardWidth, rowHeight } = gridMetrics;

  const preview = (
    <Fieldset cols={1}>
      <Form.Field<IWidgetProps>
        key="demo"
        dataid={dataid}
        component={Widget}
        gridMetrics={{ gridWidth, cardWidth, rowHeight: rowHeight }}
        forceUpdate={true}
        handleDrillFilter={handleDrillFilter}
      />
    </Fieldset>
  );

  return (
    <div style={{ height: window.innerHeight * 0.9 }}>
      <BasicHeader>Edit Widget {selectedId}</BasicHeader>
      <ModalBottomButtonContainer>
        <Button onClick={onClose} data-testid="edit-widget-close-button">
          Close
        </Button>
        <PrimaryButton
          onClick={() => {
            connectedForm.submit?.();
            onClose();
          }}
          data-testid="edit-widget-save-close-button"
        >
          Save Dashboard & Close
        </PrimaryButton>
      </ModalBottomButtonContainer>
      <Form.TabPanel>
        <Form.TabContent id="data" label="Data">
          <Form.Fieldset cols={1}>
            <Form.Legend label={'General'} />
            <Form.Field
              dataid={`${dataid}.label`}
              component={input.BlurTextField}
              label={'Label'}
              autoFocus={true}
              mandatory={true}
              data-testid="edit-widget-label"
            />
            {targetField}
          </Form.Fieldset>
          {editFields}
          {preview}
        </Form.TabContent>
        <Form.TabContent id="style" label="Style">
          <WidgetStyleConfiguration value={value} dataid={dataid} />
          {preview}
        </Form.TabContent>
      </Form.TabPanel>
    </div>
  );
};

// eslint-disable-next-line complexity
const filterCostAndProfitFields = (fields: IObjectField[], costViewPermission: CostViewPermission): IObjectField[] => {
  let filteredFields: IObjectField[] = [...fields];

  if (costViewPermission == CostViewPermission.Haggle) {
    // remove costs and profits except for haggle fields
    filteredFields = filteredFields.filter(
      (field) => !field.fieldName.toLowerCase().includes('cost') || field.fieldName.toLowerCase().includes('haggle')
    );
    filteredFields = filteredFields.filter(
      (field) => !field.fieldName.toLowerCase().includes('profit') || field.fieldName.toLowerCase().includes('haggle')
    );
  } else if (costViewPermission === CostViewPermission.None) {
    // remove all costs and profits
    filteredFields = filteredFields.filter(
      (field) => !field.fieldName.toLowerCase().includes('cost') && !field.fieldName.toLowerCase().includes('profit')
    );
  }

  return filteredFields;
};

const useCostViewPermission = (): CostViewPermission => {
  const companyUser = useSelector((store: any) => store.app?.fullUserDetails?.companyUser);
  return companyUser?.costViewPermission || CostViewPermission.None;
};

const usePermissionFilteredFields = (fields: IObjectField[]): IObjectField[] => {
  const costViewPermission = useCostViewPermission();

  const filteredFields = useMemo(() => {
    return filterCostAndProfitFields(fields, costViewPermission);
  }, [costViewPermission, fields]);

  return filteredFields;
};

// eslint-disable-next-line complexity

const get2DimensionalExampleData: () => Array<NivoPieData> = () => {
  const values = [20000, 50000, 45233, 32321, 52312, 33232, 43432, 32234, 0, 0, 0, 0];
  const labels = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

  const data: Array<NivoPieData> = [];
  for (let i = 0; i < values.length; i++) {
    data.push({
      id: labels[i],
      label: labels[i],
      value: values[i],
      color: OGL_COLOURS[i],
    });
  }

  return data;
};

const EXAMPLE_LAYOUT = [
  {
    i: 'a',
    x: 0,
    y: 0,
    w: 2,
    h: 3,
    static: true,
  },
  {
    i: 'b',
    x: 2,
    y: 0,
    w: 3,
    h: 2,
    static: true,
    type: 'TWO_DIMENSION',
  },
  {
    i: 'c',
    x: 0,
    y: 3,
    w: 2,
    h: 3,
    static: true,
    type: 'WORKSPACE',
  },
  {
    i: 'd',
    x: 2,
    y: 2,
    w: 3,
    h: 4,
    static: true,
    type: 'ANALYTICS',
  },
];

const SectionDiv = ({ rowHeight, children, width = '50%', style = {} as React.CSSProperties }) => (
  <div
    style={{
      position: 'relative',
      height: `${rowHeight}px`,
      boxSizing: 'border-box',
      flex: '0 0 auto',
      paddingRight: '16px',
      paddingLeft: '16px',
      flexBasis: width,
      maxWidth: width,
      ...style,
    }}
  >
    {children}
  </div>
);

enum WidgetDataType {
  ONE_DIMENSION = 'ONE_DIMENSION',
  TWO_DIMENSION = 'TWO_DIMENSION',
  WORKSPACE = 'WORKSPACE',
  ANALYTICS = 'ANALYTICS',
}
//IDashboardWidgetDefaultQueryResult

type IAddSectionProps = {
  gridMetrics: {
    gridWidth: number;
    cardWidth: number;
    rowHeight: number;
  } | null;
  data: IDashboardWidgetDefaultQueryResult;
  onClose: () => void;
  addNewWidget: (uuid?: string | null) => void;
  onChange: (value: any) => void;
};

const AddSection = ({ gridMetrics, data, onClose, addNewWidget, onChange }: IAddSectionProps) => {
  if (!gridMetrics) return null;
  const { gridWidth, rowHeight, cardWidth } = gridMetrics;

  const addWidget = (newWidgetDataType: WidgetDataType) => {
    const newWidget = { ...(data?.dashboardWidgetDefault || {}) } as IDashboardWidget;
    newWidget.label = 'New Widget';
    newWidget.x = 0;
    newWidget.y = 10;
    switch (newWidgetDataType) {
      case WidgetDataType.ONE_DIMENSION:
        newWidget.type = DashboardWidgetType.Number;
        newWidget.w = 1;
        newWidget.h = 1;
        break;
      case WidgetDataType.TWO_DIMENSION:
        newWidget.type = DashboardWidgetType.Bar;
        newWidget.w = 2;
        newWidget.h = 2;
        break;
      case WidgetDataType.WORKSPACE:
        newWidget.type = DashboardWidgetType.Workspace;
        newWidget.w = 2;
        newWidget.h = 3;
        break;
      case WidgetDataType.ANALYTICS:
        newWidget.type = DashboardWidgetType.Analytics;
        newWidget.w = 3;
        newWidget.h = 3;
        break;
      default:
        throw new Error('Invalid Data Type');
    }

    addNewWidget(newWidget.uuid);
    onChange(newWidget);
  };

  return (
    <>
      <BasicHeader>Add Widget</BasicHeader>
      <ModalBottomButtonContainer>
        <Button onClick={onClose} data-testid="add-widget-close-button">
          Close
        </Button>
      </ModalBottomButtonContainer>
      <GridContainer>
        <GridLayout
          className="layout"
          layout={EXAMPLE_LAYOUT}
          cols={5}
          rowHeight={rowHeight}
          width={gridWidth}
          autosize={false}
        >
          <div key="a" onClick={() => addWidget(WidgetDataType.ONE_DIMENSION)} data-testid="add-widget-total-widget">
            <DashboardCard label="Total Widget" data-testid="add-widget-total-widget-card">
              <Row>
                <SectionDiv rowHeight={rowHeight} style={{ marginTop: '25px' }}>
                  <NumberWidget value={842} valueMask={'0,0'} />
                </SectionDiv>
                <SectionDiv rowHeight={rowHeight}>
                  <NivoProgress value={75} target={100} subTitle="Subtitle" />
                </SectionDiv>
                <SectionDiv rowHeight={rowHeight} style={{ marginTop: '40px' }}>
                  <NivoGauge value={75} target={100} subTitle="Subtitle" height={rowHeight * 1.25} semi={true} />
                </SectionDiv>
                <SectionDiv rowHeight={rowHeight} style={{ marginTop: '35px' }}>
                  <NivoGauge value={75} target={100} subTitle="Circular Gauge" height={rowHeight * 1.25} />
                </SectionDiv>
              </Row>
            </DashboardCard>
          </div>
          <div key="b" onClick={() => addWidget(WidgetDataType.TWO_DIMENSION)} data-testid="add-widget-2d-chart">
            <DashboardCard label="2 Dimensional Chart" data-testid="add-widget-2d-chart-card">
              <Row>
                <SectionDiv rowHeight={rowHeight * 2} width={'40%'}>
                  <div
                    style={{
                      height: `${rowHeight * 2 - 32}px`,
                      width: `${(cardWidth * 3 * 2) / 5 - 32}px`,
                    }}
                  >
                    <NivoPie type={'PIE'} data={get2DimensionalExampleData()} />
                  </div>
                </SectionDiv>
                <SectionDiv rowHeight={rowHeight * 2} width={'60%'}>
                  <NivoBar data={get2DimensionalExampleData()} cardPixelWidth={(cardWidth * 3 * 2) / 5 - 32} />
                </SectionDiv>
              </Row>
            </DashboardCard>
          </div>
          <div key="c" onClick={() => addWidget(WidgetDataType.WORKSPACE)} data-testid="add-widget-workspace">
            <DashboardCard label="Workspace" data-testid="add-widget-workspace-card">
              <DiaryEventWorkspace
                miniGrid={true}
                columns={['subject', 'closed', 'eventStart']}
                contentHeight={rowHeight * 2 - 25}
                contentWidth={cardWidth * 2 - 45}
                location={{ pathname: '/' }}
                match={{ path: '/', url: '/' }}
              />
            </DashboardCard>
          </div>
          <div key="d" onClick={() => addWidget(WidgetDataType.ANALYTICS)} data-testid="add-widget-sales-analytics">
            <DashboardCard label="Sales Analytics" data-testid="add-widget-sales-analytics-card">
              <SalesInvoiceAnalysis graphOnly={true} graphHeight={450} />
            </DashboardCard>
          </div>
        </GridLayout>
      </GridContainer>
    </>
  );
};

type IAddWidgetProps = {
  addNewWidget: (uuid: string) => void;
  data: IDashboardWidgetDefaultQueryResult;
  dataid: string;
  formChange: (
    field: string,
    value: any,
    mutationId?: string
  ) => {
    type: string;
    meta: { form: string; field: string; touch: boolean; mutationId?: string };
    payload: any;
  };
  onChange: (value: any) => void;
  onClose: () => void;
  selectedId: number; //???
  value: IDashboardWidget;
};

const AddWidget = ({
  addNewWidget,
  data,
  dataid,
  formChange,
  onChange,
  onClose,
  selectedId,
  value,
}: IAddWidgetProps) => {
  const dispatch = useDispatch();

  const contentContainerRef = useRef<any>();

  const [gridMetrics, setGridMetrics] = useState<{
    gridWidth: number;
    cardWidth: number;
    rowHeight: number;
  } | null>(null);

  //Wrap with callback so we have the same reference when adding/removing the listener.
  const onResize = useCallback(() => {
    const newGridWidth = window.innerWidth * 0.75;
    const newCardWidth = Math.round(newGridWidth / 5);
    const newRowHeight = Math.round((window.innerHeight - 20) / 7);
    setGridMetrics({
      gridWidth: newGridWidth,
      cardWidth: newCardWidth,
      rowHeight: newRowHeight,
    });
  }, []);

  useEffect(() => {
    onResize();
  }, [contentContainerRef.current]);

  useEffect(() => {
    window.addEventListener('resize', onResize, false);
    onResize();

    return () => {
      window.removeEventListener('resize', onResize, false);
    };
  }, [onResize]);

  type IDrillHistory = Array<{
    dimension: { value: null; title: string; description: string };
    dimensionFilters: [];
    history: IDrillHistory;
  }>;

  const handleDrillFilter = (drillProps: {
    dimensionFilters: [{ dimension: string; id: number }];
    dimension: { title: string; value: string };
    history: IDrillHistory;
  }) => {
    let historyAsSingleArray: Array<ISalesAnalyticsHistoryInput> = [];
    if (drillProps.history && drillProps.history.length > 0) {
      historyAsSingleArray = drillProps.history.map((history) => {
        return {
          dimension: history.dimension,
          dimensionFilters: history.dimensionFilters.map(({ dimension, id }) => ({
            dimension,
            dimensionId: id,
          })),
        };
      });
    }
    if (drillProps.dimension && drillProps.dimensionFilters) {
      historyAsSingleArray.push({
        dimension: drillProps.dimension,
        dimensionFilters: drillProps.dimensionFilters.map(({ dimension, id }) => ({
          dimension,
          dimensionId: id,
        })),
      });
    }

    const previousHistory = value.salesAnalyticsHistory;
    if (previousHistory && previousHistory.length) {
      //If previously persisted, need to keep the entries but mark them as deleted.
      //Otherwise they get re-merged which is not desired behaviour.
      previousHistory.forEach((entry) => {
        if (entry?.id) {
          historyAsSingleArray.push({
            ...(entry as ISalesAnalyticsHistoryInput),
            deleted: true,
          });
        }
      });
    }

    dispatch(formChange(`${dataid}.salesAnalyticsHistory`, historyAsSingleArray));
  };

  if (value) {
    return (
      <div ref={contentContainerRef}>
        <EditSection
          dataid={dataid}
          formChange={formChange}
          gridMetrics={gridMetrics}
          value={value}
          selectedId={selectedId}
          onClose={onClose}
          handleDrillFilter={handleDrillFilter}
        />
      </div>
    );
  }

  return (
    <div ref={contentContainerRef}>
      <AddSection
        data={data}
        onClose={onClose}
        gridMetrics={gridMetrics}
        addNewWidget={addNewWidget}
        onChange={onChange}
      />
    </div>
  );
};

export default compose(withContainer.query(WidgetDefault))(AddWidget);
